import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { X, Plus, Trash2, Image, Video, Eye, EyeOff, Upload, Link, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PromoSettings = ({ token, onClose }) => {
  const [promoContent, setPromoContent] = useState([]);
  const [inputMode, setInputMode] = useState('upload'); // 'url' or 'upload'
  const [type, setType] = useState('image');
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState(30); // Default 30 seconds
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // TV Settings
  const [promoDelayAfterToken, setPromoDelayAfterToken] = useState(10);
  const [promoDelayNoPatient, setPromoDelayNoPatient] = useState(5);
  const [showDoctorPhoto, setShowDoctorPhoto] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  // Footer announcement lines
  const [annLine1, setAnnLine1] = useState('Welcome to K.M Multi-Speciality Clinic');
  const [annLine2, setAnnLine2] = useState('Please wait for your token number');
  const [scroll1Enabled, setScroll1Enabled] = useState(true);
  const [scroll2Enabled, setScroll2Enabled] = useState(true);
  const [savingAnnouncements, setSavingAnnouncements] = useState(false);
  
  // Editing state for individual promo durations
  const [editingDuration, setEditingDuration] = useState({});

  const fetchPromoContent = async () => {
    try {
      const response = await axios.get(`${API}/promo-content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromoContent(response.data);
    } catch (error) {
      console.error('Error fetching promo content:', error);
    }
  };

  const fetchTVSettings = async () => {
    try {
      const response = await axios.get(`${API}/public/tv-settings`);
      setPromoDelayAfterToken(response.data.promo_delay_after_token || 10);
      setPromoDelayNoPatient(response.data.promo_delay_no_patient || 5);
      setShowDoctorPhoto(response.data.show_doctor_photo !== false);
    } catch (error) {
      console.error('Error fetching TV settings:', error);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/public/announcements`);
      setAnnLine1(response.data.line1 || 'Welcome to K.M Multi-Speciality Clinic');
      setAnnLine2(response.data.line2 || 'Please wait for your token number');
      setScroll1Enabled(response.data.scroll1_enabled !== false);
      setScroll2Enabled(response.data.scroll2_enabled !== false);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };

  const saveAnnouncements = async () => {
    setSavingAnnouncements(true);
    try {
      await axios.put(
        `${API}/announcements`,
        {
          line1: annLine1,
          line2: annLine2,
          scroll1_enabled: scroll1Enabled,
          scroll2_enabled: scroll2Enabled
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Footer announcements saved!');
    } catch (error) {
      toast.error('Failed to save announcements');
    } finally {
      setSavingAnnouncements(false);
    }
  };

  const saveTVSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(
        `${API}/tv-settings`,
        {
          promo_delay_after_token: promoDelayAfterToken,
          promo_delay_no_patient: promoDelayNoPatient,
          show_doctor_photo: showDoctorPhoto
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('TV settings saved!');
    } catch (error) {
      toast.error('Failed to save TV settings');
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => {
    fetchPromoContent();
    fetchTVSettings();
    fetchAnnouncements();
  }, []);

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const videoTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    const allowedTypes = [...imageTypes, ...videoTypes];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: Images (JPEG, PNG, GIF, WebP) or Videos (MP4, WebM, MOV)');
      return;
    }

    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 50MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/promo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // URL is returned as /api/uploads/promo/filename, need full path
      const uploadedUrl = `${BACKEND_URL}${response.data.url}`;
      setUrl(uploadedUrl);
      setType(response.data.type);
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleAdd = async () => {
    if (!url.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/promo-content`,
        { type, url, title: title.trim() || null, duration },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Promotional content added');
      setUrl('');
      setTitle('');
      setDuration(30);
      fetchPromoContent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add content');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (contentId) => {
    if (!confirm('Delete this promotional content?')) return;

    try {
      await axios.delete(`${API}/promo-content/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Content deleted');
      fetchPromoContent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggle = async (contentId) => {
    try {
      await axios.patch(`${API}/promo-content/${contentId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Content visibility toggled');
      fetchPromoContent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Toggle failed');
    }
  };

  const handleUpdateDuration = async (contentId, newDuration) => {
    try {
      await axios.put(
        `${API}/promo-content/${contentId}`,
        { duration: parseInt(newDuration) || 30 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Duration updated');
      setEditingDuration({});
      fetchPromoContent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            TV Display Promotional Content
          </h2>
          <Button onClick={onClose} variant="ghost" size="icon">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <p className="text-muted-foreground text-sm mb-6">
          Add images or videos to display on the TV screen when no patient is being served.
          Content will auto-rotate every 8 seconds.
        </p>

        {/* TV Display Settings */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold mb-4 text-blue-900">TV Display Settings</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="delay-token">Promo delay after token (seconds)</Label>
                <Input
                  id="delay-token"
                  type="number"
                  min="1"
                  max="120"
                  value={promoDelayAfterToken}
                  onChange={(e) => setPromoDelayAfterToken(parseInt(e.target.value) || 10)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Wait time before showing promos after a token is displayed
                </p>
              </div>
              <div>
                <Label htmlFor="delay-no-patient">Promo delay when no patient (seconds)</Label>
                <Input
                  id="delay-no-patient"
                  type="number"
                  min="1"
                  max="120"
                  value={promoDelayNoPatient}
                  onChange={(e) => setPromoDelayNoPatient(parseInt(e.target.value) || 5)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Wait time before showing promos when no patient is being served
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-white rounded border">
              <div>
                <Label>Show Doctor Photo on TV Display</Label>
                <p className="text-xs text-muted-foreground">Toggle doctor photo visibility in TV header</p>
              </div>
              <Button
                type="button"
                variant={showDoctorPhoto ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowDoctorPhoto(!showDoctorPhoto)}
              >
                {showDoctorPhoto ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
                {showDoctorPhoto ? 'ON' : 'OFF'}
              </Button>
            </div>
            
            <Button onClick={saveTVSettings} disabled={savingSettings} className="w-full">
              {savingSettings ? 'Saving...' : 'Save TV Settings'}
            </Button>
          </div>
        </div>

        {/* Footer Announcement Lines */}
        <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200" data-testid="tv-footer-settings">
          <h3 className="text-lg font-semibold mb-1 text-green-900">Footer Announcement Lines</h3>
          <p className="text-xs text-green-700 mb-4">Bottom scrolling banners on the TV display</p>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="ann-line1" className="font-semibold">Line 1 (yellow / top)</Label>
                <Button
                  type="button"
                  variant={scroll1Enabled ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setScroll1Enabled(!scroll1Enabled)}
                  data-testid="ann-scroll1-toggle"
                >
                  {scroll1Enabled ? '⟶ Scrolling' : 'Static'}
                </Button>
              </div>
              <Input
                id="ann-line1"
                value={annLine1}
                onChange={(e) => setAnnLine1(e.target.value)}
                placeholder="Welcome to K.M Multi-Speciality Clinic"
                maxLength={200}
                data-testid="ann-line1-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="ann-line2" className="font-semibold">Line 2 (blue / bottom)</Label>
                <Button
                  type="button"
                  variant={scroll2Enabled ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setScroll2Enabled(!scroll2Enabled)}
                  data-testid="ann-scroll2-toggle"
                >
                  {scroll2Enabled ? '⟶ Scrolling' : 'Static'}
                </Button>
              </div>
              <Input
                id="ann-line2"
                value={annLine2}
                onChange={(e) => setAnnLine2(e.target.value)}
                placeholder="Please wait for your token number"
                maxLength={200}
                data-testid="ann-line2-input"
              />
            </div>
            <Button
              onClick={saveAnnouncements}
              disabled={savingAnnouncements}
              className="w-full bg-green-700 hover:bg-green-800 text-white"
              data-testid="save-announcements-btn"
            >
              {savingAnnouncements ? 'Saving...' : 'Save Footer Lines'}
            </Button>
          </div>
        </div>

        {/* Add Form */}
        <div className="mb-6 p-4 bg-slate-50 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">Add Content</h3>
          <div className="space-y-4">
            <div>
              <Label>Content Type</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant={type === 'image' ? 'default' : 'outline'}
                  onClick={() => setType('image')}
                  className="flex-1"
                >
                  <Image className="h-4 w-4 mr-2" />
                  Image
                </Button>
                <Button
                  type="button"
                  variant={type === 'video' ? 'default' : 'outline'}
                  onClick={() => setType('video')}
                  className="flex-1"
                >
                  <Video className="h-4 w-4 mr-2" />
                  Video
                </Button>
              </div>
            </div>
            
            {/* Input Mode Toggle */}
            <div>
              <Label>Add Content By</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant={inputMode === 'upload' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setInputMode('upload')}
                  className="flex-1"
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Upload File
                </Button>
                <Button
                  type="button"
                  variant={inputMode === 'url' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setInputMode('url')}
                  className="flex-1"
                >
                  <Link className="h-4 w-4 mr-1" />
                  Use URL
                </Button>
              </div>
            </div>

            {inputMode === 'upload' ? (
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Choose Image or Video'}
                </Button>
                <p className="text-xs text-muted-foreground mt-1">
                  Max 50MB. Images: JPEG, PNG, GIF, WebP. Videos: MP4, WebM, MOV
                </p>
                {url && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                    ✓ File uploaded: {url.split('/').pop()}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <Label htmlFor="promo-url">{type === 'image' ? 'Image URL' : 'Video URL'}</Label>
                <Input
                  id="promo-url"
                  type="url"
                  placeholder={type === 'image' ? 'https://example.com/promo.jpg' : 'https://example.com/promo.mp4'}
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="mt-1"
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="promo-title">Title (Optional)</Label>
              <Input
                id="promo-title"
                type="text"
                placeholder="e.g., Special Offer, Health Tip"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="promo-duration">Display Duration (seconds)</Label>
              <Input
                id="promo-duration"
                type="number"
                min="5"
                max="300"
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value) || 30)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">How long to show this content (default: 30 seconds)</p>
            </div>
            
            {/* Preview */}
            {url && (
              <div className="mt-2">
                <Label>Preview</Label>
                {type === 'image' ? (
                  <img 
                    src={url} 
                    alt="Preview" 
                    className="mt-1 max-h-32 rounded border"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                ) : (
                  <video 
                    src={url} 
                    className="mt-1 max-h-32 rounded border"
                    controls
                    muted
                  />
                )}
              </div>
            )}
            
            <Button onClick={handleAdd} disabled={loading || uploading || !url} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              {loading ? 'Adding...' : 'Add Content'}
            </Button>
          </div>
        </div>

        {/* Content List */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Current Content ({promoContent.length})</h3>
          {promoContent.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              No promotional content added yet. Add images or videos to display on the TV screen.
            </p>
          ) : (
            <div className="space-y-3">
              {promoContent.map((content) => (
                <div
                  key={content.id}
                  className={`flex items-center gap-4 p-3 rounded-lg border ${content.is_active ? 'bg-white border-slate-200' : 'bg-slate-100 border-slate-300 opacity-60'}`}
                >
                  {/* Thumbnail */}
                  <div className="w-20 h-14 rounded overflow-hidden bg-slate-200 flex-shrink-0">
                    {content.type === 'image' ? (
                      <>
                        <img 
                          src={content.url} 
                          alt={content.title || 'Promo'} 
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.querySelector('.img-fallback')?.classList.remove('hidden'); }}
                        />
                        <div className="img-fallback hidden w-full h-full flex items-center justify-center text-slate-400">Image</div>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-500">
                        <Video className="h-6 w-6" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{content.title || 'Untitled'}</p>
                    <p className="text-xs text-muted-foreground truncate">{content.url}</p>
                    <div className="flex gap-2 mt-1 flex-wrap items-center">
                      <span className={`text-xs px-2 py-0.5 rounded ${content.type === 'image' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                        {content.type}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded ${content.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>
                        {content.is_active ? 'Active' : 'Hidden'}
                      </span>
                      {/* Duration control */}
                      {editingDuration[content.id] !== undefined ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            min="5"
                            max="300"
                            value={editingDuration[content.id]}
                            onChange={(e) => setEditingDuration({...editingDuration, [content.id]: e.target.value})}
                            className="w-16 h-6 text-xs px-1"
                          />
                          <span className="text-xs">sec</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-green-600"
                            onClick={() => handleUpdateDuration(content.id, editingDuration[content.id])}
                          >
                            ✓
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-red-600"
                            onClick={() => setEditingDuration({})}
                          >
                            ✕
                          </Button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditingDuration({[content.id]: content.duration || 30})}
                          className="text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-700 hover:bg-orange-200"
                        >
                          {content.duration || 30}s
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1">
                    <Button
                      onClick={() => handleToggle(content.id)}
                      variant="ghost"
                      size="icon"
                      title={content.is_active ? 'Hide' : 'Show'}
                    >
                      {content.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      onClick={() => handleDelete(content.id)}
                      variant="ghost"
                      size="icon"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default PromoSettings;
